import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Package, MapPin, User, Clock, AlertCircle, 
  CheckCircle, XCircle, MessageSquare, Plus, History, Info 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const DeviceDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    fetchDeviceDetails();
  }, [deviceId]);

  const fetchDeviceDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/trc/devices/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDevice(response.data.device);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch device details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      setAddingNote(true);
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await axios.post(
        `${API_URL}/api/trc/devices/${deviceId}/add-note`,
        {
          note: newNote,
          added_by: user.name || user.employee_id || 'User'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewNote('');
      setShowNoteModal(false);
      fetchDeviceDetails(); // Refresh to show new note
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'received': 'bg-blue-100 text-blue-800',
      'binned': 'bg-purple-100 text-purple-800',
      'assigned_to_engineer': 'bg-yellow-100 text-yellow-800',
      'iqc_complete': 'bg-indigo-100 text-indigo-800',
      'ntf': 'bg-green-100 text-green-800',
      'tf_pna': 'bg-orange-100 text-orange-800',
      'rfr': 'bg-cyan-100 text-cyan-800',
      'repair_in_progress': 'bg-amber-100 text-amber-800',
      'repair_completed': 'bg-teal-100 text-teal-800',
      'fqc_pass': 'bg-green-100 text-green-800',
      'fqc_fail_rtr': 'bg-red-100 text-red-800',
      'ber': 'bg-gray-100 text-gray-800',
      'nr': 'bg-gray-100 text-gray-800',
      'asc_repair': 'bg-pink-100 text-pink-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: ZOBOX_BLUE }}></div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 mr-2" />
            <span>{error || 'Device not found'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" style={{ color: ZOBOX_BLUE }} />
          </button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>
              Device Details
            </h1>
            <p className="text-gray-600 mt-1">{device.did}</p>
          </div>
        </div>
        <button
          onClick={() => setShowNoteModal(true)}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: ZOBOX_BLUE }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Note
        </button>
      </div>

      {/* Device Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Info className="w-5 h-5 mr-2" />
            Basic Information
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Device ID (DID):</span>
              <span className="font-semibold">{device.did}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">TRC ID:</span>
              <span className="font-semibold">{device.trc_device_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Brand:</span>
              <span className="font-semibold">{device.brand}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Model:</span>
              <span className="font-semibold">{device.model}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Category:</span>
              <span className="font-semibold">{device.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">SKU:</span>
              <span className="font-semibold">{device.sku || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">ZUPC:</span>
              <span className="font-semibold">{device.zupc || '-'}</span>
            </div>
          </div>
        </div>

        {/* Status Information */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4 flex items-center" style={{ color: ZOBOX_BLUE }}>
            <Package className="w-5 h-5 mr-2" />
            Status & Location
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Current Status:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(device.status)}`}>
                {formatStatus(device.status)}
              </span>
            </div>
            {device.is_sos && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Priority:</span>
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  SOS PRIORITY
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Current Bin:</span>
              <span className="font-semibold flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {device.current_bin || 'Not Binned'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Assigned Engineer:</span>
              <span className="font-semibold flex items-center">
                <User className="w-4 h-4 mr-1" />
                {device.assigned_engineer || 'Unassigned'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">IQC Grade:</span>
              <span className="font-semibold">{device.iqc_grade || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">FQC Grade:</span>
              <span className="font-semibold">{device.fqc_grade || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Received At:</span>
              <span className="font-semibold flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {formatDateTime(device.created_at)}
              </span>
            </div>
            {device.repair_time_minutes && (
              <div className="flex justify-between">
                <span className="text-gray-600">Repair Time:</span>
                <span className="font-semibold">{device.repair_time_minutes} minutes</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer Complaint & Condition Notes */}
      {(device.customer_complaint || device.condition_notes) && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4" style={{ color: ZOBOX_BLUE }}>
            Additional Information
          </h2>
          {device.customer_complaint && (
            <div className="mb-4">
              <p className="text-gray-600 text-sm font-medium mb-2">Customer Complaint:</p>
              <p className="text-gray-800 bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                {device.customer_complaint}
              </p>
            </div>
          )}
          {device.condition_notes && (
            <div>
              <p className="text-gray-600 text-sm font-medium mb-2">Condition Notes:</p>
              <p className="text-gray-800 bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                {device.condition_notes}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Device History Timeline */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-6 flex items-center" style={{ color: ZOBOX_BLUE }}>
          <History className="w-5 h-5 mr-2" />
          Device History
        </h2>
        <div className="space-y-4">
          {device.history && device.history.length > 0 ? (
            device.history.map((event, index) => (
              <div key={index} className="flex">
                <div className="flex flex-col items-center mr-4">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: ZOBOX_BLUE }}
                  />
                  {index !== device.history.length - 1 && (
                    <div className="w-0.5 h-full bg-gray-300 mt-1" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold" style={{ color: ZOBOX_BLUE }}>
                        {event.action.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDateTime(event.timestamp)}
                      </span>
                    </div>
                    <p className="text-gray-700">{event.details}</p>
                    {event.performed_by && (
                      <p className="text-sm text-gray-500 mt-2">
                        By: {event.performed_by}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-8">
              <History className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No history available</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>
                Add Note
              </h3>
              <button
                onClick={() => setShowNoteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note here..."
              className="w-full border rounded-lg p-3 mb-4 h-32 resize-none focus:outline-none focus:ring-2"
              style={{ focusRing: ZOBOX_BLUE }}
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ZOBOX_BLUE }}
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceDetails;
